import { useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { LoadState } from "@/components/ui/LoadState"
import { AttendanceTable, type AttendanceColumn } from "@/components/cccd/AttendanceTable"
import { AttendanceScopeFilter, type ScopeFilterValue } from "@/components/cccd/AttendanceScopeFilter"
import { useAttendanceSummary } from "@/hooks/useAttendanceSummary"
import { cccdAttendance, type AttendanceReport, type AttendanceSummaryRow } from "@/lib/api/cccdAttendanceClient"
import { attendancePercent, filterRows, formatDay, formatRange, reportPath, sumTotals } from "@/lib/checkinEvents"
import { getErrorMessage } from "@/lib/errors"

const REPORT_TABS = [
  { label: "Escanear", to: "/cccd/checkin" },
  { label: "Reporte", to: "/cccd/checkin/report", active: true },
]

const BREADCRUMBS = [{ label: "SateLab", to: "/" }, { label: "CCCD", to: "/cccd" }, "Reporte de asistencia"]

function ConsolidatedReport() {
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useAttendanceSummary()
  const [scope, setScope] = useState<ScopeFilterValue>("all")
  const [search, setSearch] = useState("")
  const rows = useMemo(() => filterRows(data ?? [], scope, search), [data, scope, search])
  const totals = useMemo(() => sumTotals(rows), [rows])

  const columns: AttendanceColumn<AttendanceSummaryRow>[] = [
    {
      key: "name",
      label: "Evento",
      primary: true,
      render: (r) => (
        <Link to={reportPath(r)} className="block min-w-0 hover:underline focus:outline-hidden focus-visible:ring-3 focus-visible:ring-brand-500/20">
          <p className="font-medium text-gray-900 dark:text-white">{r.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{r.scope === "event" ? "Nacional" : `Regional · ${r.region ?? "sin región"}`}</p>
        </Link>
      ),
    },
    { key: "dates", label: "Fechas", render: (r) => <span className="text-xs text-gray-500 sm:whitespace-nowrap dark:text-gray-400">{formatRange(r.eventStartDate, r.eventEndDate)}</span> },
    { key: "tickets", label: "Boletos", align: "right", render: (r) => r.totalTickets, footer: totals.totalTickets },
    { key: "members", label: "Integrantes", align: "right", render: (r) => r.totalMembers, footer: totals.totalMembers },
    { key: "companions", label: "Acompañantes", align: "right", render: (r) => r.totalCompanions, footer: totals.totalCompanions },
    { key: "checkedIn", label: "Ingresos", align: "right", render: (r) => r.checkedIn, footer: totals.checkedIn },
    { key: "wristbands", label: "Manillas", align: "right", render: (r) => r.wristbands, footer: totals.wristbands },
    {
      key: "percent",
      label: "Asistencia",
      align: "right",
      render: (r) => `${attendancePercent(r.attendeesCheckedIn, r.totalTickets)}%`,
      footer: `${attendancePercent(totals.attendeesCheckedIn, totals.totalTickets)}%`,
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={BREADCRUMBS}
      title="Reporte de asistencia"
      subtitle="Boletos, ingresos y manillas consolidados por evento. Abre uno para ver el detalle por día."
      tabs={REPORT_TABS}
      metrics={[]}
    >
      <div className="space-y-6">
        <AttendanceScopeFilter scope={scope} onScopeChange={setScope} search={search} onSearchChange={setSearch} />

        {isLoading && <LoadState label="Cargando reporte..." />}

        {isError && (
          <Alert
            variant="error"
            title="No se pudo cargar el reporte"
            message={`${getErrorMessage(error)}. Intenta actualizar la página; los números no se están mostrando, no significa que no haya check-ins.`}
          />
        )}

        {data && (
          <AttendanceTable
            columns={columns}
            rows={rows}
            rowKey={(r) => `${r.scope}:${r.id}`}
            caption="Asistencia consolidada por evento"
            emptyMessage={data.length === 0 ? "Aún no hay eventos registrados." : "Ningún evento coincide con el filtro."}
            footerLabel="Total"
            onRowClick={(r) => navigate(reportPath(r))}
          />
        )}
      </div>
    </ModuleLayout>
  )
}

type DayRow = AttendanceReport["days"][number]

function EventReport({ eventId, regionalEventId }: { eventId?: string; regionalEventId?: string }) {
  const scope = eventId ? { eventId } : { regionalEventId }
  const kind = eventId ? "event" : "regional"
  const scopeId = eventId ?? regionalEventId ?? ""
  const { data: summary } = useAttendanceSummary()
  const row = summary?.find((r) => r.scope === kind && r.id === scopeId)

  const { data: report, isLoading, isError, error } = useQuery({
    queryKey: ["attendance-report", `${kind}:${scopeId}`],
    queryFn: () => cccdAttendance.getReport(scope),
  })

  const totals = useMemo(
    () =>
      (report?.days ?? []).reduce(
        (acc, d) => ({
          checkedIn: acc.checkedIn + d.checkedIn,
          wristbands: acc.wristbands + d.wristbands,
          members: acc.members + d.members,
          companions: acc.companions + d.companions,
        }),
        { checkedIn: 0, wristbands: 0, members: 0, companions: 0 },
      ),
    [report],
  )

  const columns: AttendanceColumn<DayRow>[] = [
    { key: "day", label: "Día", primary: true, render: (d) => <span className="font-medium text-gray-900 dark:text-white">{formatDay(d.eventDate)}</span> },
    { key: "checkedIn", label: "Ingresos", align: "right", render: (d) => d.checkedIn, footer: totals.checkedIn },
    { key: "wristbands", label: "Manillas", align: "right", render: (d) => d.wristbands, footer: totals.wristbands },
    { key: "members", label: "Integrantes", align: "right", render: (d) => d.members, footer: totals.members },
    { key: "companions", label: "Acompañantes", align: "right", render: (d) => d.companions, footer: totals.companions },
  ]

  return (
    <ModuleLayout
      breadcrumbs={BREADCRUMBS}
      title={row?.name ?? "Reporte de asistencia"}
      subtitle={row ? `${formatRange(row.eventStartDate, row.eventEndDate)} · conteo por día.` : "Conteo de ingresos, manillas e integrantes vs. acompañantes, por día."}
      tabs={REPORT_TABS}
      metrics={
        report
          ? [
              { label: "Boletos emitidos", value: String(report.totalTickets) },
              { label: "Integrantes", value: String(report.totalMembers) },
              { label: "Acompañantes", value: String(report.totalCompanions) },
            ]
          : []
      }
    >
      <div className="space-y-6">
        <Link
          to="/cccd/checkin/report"
          className="inline-flex min-h-11 items-center gap-1 rounded-lg pr-2 text-sm font-medium text-brand-500 hover:text-brand-600 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-brand-500/20"
        >
          <span aria-hidden="true">←</span> Todos los eventos
        </Link>

        {isLoading && <LoadState label="Cargando reporte..." />}

        {isError && (
          <Alert
            variant="error"
            title="No se pudo cargar el reporte"
            message={`${getErrorMessage(error)}. Intenta actualizar la página; los números no se están mostrando, no significa que no haya check-ins.`}
          />
        )}

        {report && (
          <AttendanceTable
            columns={columns}
            rows={report.days}
            rowKey={(d) => d.eventDate}
            caption="Asistencia por día"
            emptyMessage="Este evento no tiene fechas registradas."
            footerLabel="Total"
          />
        )}
      </div>
    </ModuleLayout>
  )
}

export default function CheckInReportPage() {
  const { eventId, regionalEventId } = useParams()
  if (eventId || regionalEventId) return <EventReport eventId={eventId} regionalEventId={regionalEventId} />
  return <ConsolidatedReport />
}
